import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import moment from 'moment-timezone';
import CalendarUtils from 'services/CalendarUtils';
import { AdminImage, PhysicianImage } from 'components/Common/UserImage';
import DefaultButton from 'components/Utils/NewDefaultButton';
import PerformanceUtils from 'services/PerformanceUtils';
import DefaultModal from 'components/Utils/DefaultModal';
import ShiftTemplatesEditor, { ShiftTemplateList } from 'components/Schedule/ShiftTemplatesEditor';
import Icon from 'components/Utils/NewIcon';
import EditShift from 'components/Schedule/ShiftsEditor/EditShift';
import './style.scss';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import UnreadBadge from 'components/Common/UnreadBadge';
import Vacations from 'components/Schedule/Vacations';
import StaffUtils, { PERMISSIONS } from 'services/StaffUtils';
import Event from 'components/Schedule/Events';
import TimeZoneAgnosticBigCalendar from 'components/Schedule/TimeZoneAgnosticBigCalendar';
import ClinicSelector from 'components/Utils/ClinicSelector';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import ShiftUtils from 'services/ShiftUtils';
import { Dropdown } from 'antd';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import MiscUtils from 'services/MiscUtils';
import ShiftBadge from 'components/Schedule/ShiftsEditor/ShiftBadge';
import { useMediaQuery } from '@material-ui/core';
import { DefaultSwitch } from 'services/UIUtils';
import i18n from '../../../services/i18n';

const daysOfWeek = [0, 1, 2, 3, 4, 5, 6];
const SHIFT_DATE_FORMAT = 'YYYY/MM/DD';

const VIEWS = {
  week: 'week',
  month: 'month',
};

const FILL_KEYS = {
  fillWeek: 'Fill Week',
  repeatWeek: 'Repeat Week...',
};

const DELETE_KEYS = {
  deleteWeek: 'Delete Week',
  deleteDates: 'Delete Dates...',
};

const ShiftsEditor = () => {
  const dispatch = useAppDispatch();

  const shiftsSelector = useAppSelector((state) => state.shifts);
  const authentication = useAppSelector((state) => state.authentication);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const admins = useAppSelector((state) => state.admins.byId);

  const allShifts = shiftsSelector.shifts;
  const { templates, pendingRequests } = shiftsSelector;

  const permissions = authentication.permissions;
  const allowShowVacations = permissions.includes(PERMISSIONS.shift_manager);

  const [today] = useState(moment());
  const [view, setView] = useState(VIEWS.week);
  const [day, setDay] = useState(getFirstWeekDay());
  const [selectedTemplate, setSelectedTemplate] = useState(undefined);
  const [unsavedShifts, setUnsavedShifts] = useState({});
  const [savingShifts, setSavingShifts] = useState(false);
  const [hours, setHours] = useState({});
  const [editTemplatesModal, setEditTemplatesModal] = useState(false);
  const [shiftEdit, setShiftEdit] = useState();
  const [deleteConfirmation, setDeleteConfirmation] = useState();
  const [deletingShift, setDeletingShift] = useState(false);
  const [showVacation, setShowVacation] = useState();
  const [showShifts, setShowShifts] = useState([]);
  const [clinicId, setClinicId] = useState(authentication.user.clinic_id);
  const [showFillModal, setShowFillModal] = useState();
  const [showTemplatesOnMobile, setShowTemplatesOnMobile] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    dispatch(api.Shifts.getShiftTemplates());
    ShiftUtils.resetColors();
  }, [dispatch, clinicId]);

  const dayUnix = day.hour(12).unix();

  useEffect(() => {
    let startDate = dayUnix;
    let endDate = moment.unix(dayUnix).add(6, 'day').unix();
    if (view === VIEWS.month) {
      startDate = moment.unix(dayUnix).startOf('month').unix();
      endDate = moment.unix(dayUnix).endOf('month').unix();
    }
    dispatch(
      api.Shifts.getDayShifts({
        clinicId,
        date: startDate,
        endDate,
      }),
    );
  }, [dispatch, dayUnix, clinicId, view]);

  const userShifts = useMemo(() => {
    let shifts = {};

    [...allShifts, ...PerformanceUtils.clone(showShifts)].forEach((shift) => {
      if (!shifts[shift.user_id]) shifts[shift.user_id] = {};

      let shiftDate = CalendarUtils.format(shift.start, SHIFT_DATE_FORMAT);
      let dayShifts = shifts[shift.user_id][shiftDate] || [];
      dayShifts = [...dayShifts, shift];

      shifts[shift.user_id][shiftDate] = dayShifts.filter(
        ({ clinic_id }) => clinic_id === clinicId,
      );
    });
    return shifts;
  }, [allShifts, showShifts, clinicId]);

  useEffect(() => {
    let firstDay = getFirstWeekDay(day).startOf('day');
    let lastDay = getLastWeekDay(day).endOf('day');
    let result = {};
    Object.keys(userShifts).forEach((userId) => {
      result[userId] = 0;

      let weekDates = getDatesBetween(firstDay, lastDay);

      weekDates.forEach((date) => {
        let existingShifts =
          (userShifts[userId] || {})[CalendarUtils.format(date.unix(), SHIFT_DATE_FORMAT)] || [];
        let unsaved =
          (unsavedShifts[userId] || {})[CalendarUtils.format(date.unix(), SHIFT_DATE_FORMAT)] || [];

        let unsavedIds = unsaved.map((shift) => shift.id);
        existingShifts = existingShifts.filter((shift) => !unsavedIds.includes(shift.id));

        let shifts = [...existingShifts, ...unsaved];

        shifts.forEach((shift) => {
          result[userId] += calculateHours(shift);
        });
      });
    });
    setHours(result);
  }, [userShifts, day, unsavedShifts]);

  function calculateHours(shift) {
    if (shift.non_working) return 0;
    let start = moment.unix(shift.start);
    let end = moment.unix(shift.end);
    return end.diff(start, 'hour');
  }

  function onDeleteShift(shift) {
    let existing = unwrapShifts(userShifts).filter((current) => current === shift);
    let unsaved = unwrapShifts(unsavedShifts).filter((current) => current === shift);

    if (unsaved.length > 0) {
      let shift = unsaved[0];
      removeUnsaved(shift);
    } else if (existing.length > 0) {
      setDeleteConfirmation(existing[0]);
    }
  }

  async function onDelete(shift = {}) {
    setDeletingShift(true);
    await dispatch(api.Shifts.deleteShift({ shiftId: shift.id }), {
      errorTitle: 'Shift Delete',
    });
    setDeletingShift(false);
    setDeleteConfirmation();
  }

  function addShiftFromTemplate(user, date, template) {
    let shift = ShiftUtils.templateToShift(date, template, user, clinicId);
    addUnsaved(shift);
  }

  function addUnsaved(shift) {
    let unsaved = getUpdatedUnsavedAfterInsert(shift);
    setUnsavedShifts(unsaved);
  }

  function removeUnsaved(shift) {
    let unsaved = getUpdatedUnsavedAfterRemove(shift);
    setUnsavedShifts(unsaved);
  }

  function getUpdatedUnsavedAfterInsert(shift, unsaved = unsavedShifts) {
    let id = CalendarUtils.format(shift.start, SHIFT_DATE_FORMAT);

    let userUnsaved = (unsaved[shift.user_id] || {})[id] || [];
    userUnsaved.push(shift);

    return {
      ...unsaved,
      [shift.user_id]: {
        ...(unsaved[shift.user_id] || {}),
        [id]: userUnsaved,
      },
    };
  }

  function getUpdatedUnsavedAfterRemove(shift, unsaved = unsavedShifts) {
    let lookupDate = CalendarUtils.format(shift.start, SHIFT_DATE_FORMAT);

    let values = (unsaved[shift.user_id] || {})[lookupDate] || [];
    let newValues = values.filter(
      (current) => ShiftUtils.getShiftHash(current) !== ShiftUtils.getShiftHash(shift),
    );

    return {
      ...unsaved,
      [shift.user_id]: {
        ...(unsaved[shift.user_id] || {}),
        [lookupDate]: newValues,
      },
    };
  }

  function unwrapShifts(shifts) {
    let result = [];
    Object.keys(shifts).forEach((userId) => {
      Object.keys(shifts[userId]).forEach((day) => {
        let userShifts = Object.values(shifts[userId][day]);
        result = [...result, ...PerformanceUtils.objectToArray(userShifts)];
      });
    });
    return result;
  }

  async function editShift(shift) {
    let success = await saveShift(shift);
    if (success) {
      setShiftEdit();
    }
  }

  async function saveShift(shift) {
    let result = await dispatch(api.Shifts.addOrEditShifts({ shifts: [shift] }), {
      errorTitle: 'Save Shift',
    });
    return !!result;
  }

  async function saveShifts() {
    setSavingShifts(true);
    let shifts = unwrapShifts(unsavedShifts);

    shifts = shifts.map((shift) => {
      let user = admins[shift.user_id] || physicians[shift.user_id];
      let role = StaffUtils.isPhysician(user) ? 'physician' : 'admin';

      return {
        ...shift,
        user_role: role,
        template_id: undefined,
      };
    });

    if (shifts.length > 0) {
      let result = await dispatch(api.Shifts.addOrEditShifts({ shifts }), {
        errorTitle: 'Save Shifts',
      });
      if (result) {
        setUnsavedShifts({});
      }
    }
    setSavingShifts(false);
  }

  const activeUsers = useMemo(() => {
    let sortedPhysicians = _.sortBy(PerformanceUtils.objectToArray(physicians), [
      function (user) {
        let shifts = _.flatten(PerformanceUtils.objectToArray(userShifts[user.id]));
        return shifts.length > 0 ? 0 : 1;
      },
      'first_name',
    ]);

    let sortedAdmins = _.sortBy(PerformanceUtils.objectToArray(admins), [
      function (user) {
        let shifts = _.flatten(PerformanceUtils.objectToArray(userShifts[user.id]));
        return shifts.length > 0 ? 0 : 1;
      },
      'name',
    ]);

    let active = StaffUtils.filterActiveUsers([...sortedPhysicians, ...sortedAdmins]);
    active = active.filter((user) => {
      let numberShifts = _.flatten(PerformanceUtils.objectToArray(userShifts[user.id])).length;
      return user.clinic_id === clinicId || numberShifts > 0;
    });
    return active;
  }, [physicians, admins, userShifts, clinicId]);

  let currentPeriodString = getCurrentPeriodDescription(day, view);

  let numberUnsaved = unwrapShifts(unsavedShifts).length;

  let className = 'directory shifts-editor';
  let buttonText = `Save ${numberUnsaved} Shifts`;

  function onClickUser(userId) {
    setShowVacation(userId);
  }

  function goToShiftWeek(shift) {
    if (shift) {
      setDay(getFirstWeekDay(moment.unix(shift.start || shift.date)));
    }
  }

  function toggleView() {
    if (view === VIEWS.month) {
      setView(VIEWS.week);
      setDay(getFirstWeekDay(day));
    } else setView(VIEWS.month);
  }

  function onFillDropdownClick(event, user) {
    if (event.key === FILL_KEYS.fillWeek) {
      fillWeek(user);
    } else if (event.key === FILL_KEYS.repeatWeek) {
      setShowFillModal(user);
    }
  }

  async function onDeleteDropdownClick(event, user) {
    if (event.key === DELETE_KEYS.deleteDates) {
      setShowDeleteModal(user);
    } else if (event.key === DELETE_KEYS.deleteWeek) {
      await deleteWeek(user);
    }
  }

  function onApplyRepeatWeekUntil(fromDate, date, user) {
    const templatesByDayOfWeek = {};
    _.range(7).forEach((it) => {
      const date = day.clone().add(it, 'days');
      const str = CalendarUtils.format(date.unix(), SHIFT_DATE_FORMAT);
      const saved = userShifts[user.id]?.[str] || [];
      const unsaved = unsavedShifts[user.id]?.[str] || [];

      templatesByDayOfWeek[date.day()] = [...saved, ...unsaved]
        .flatMap(_.identity)
        .filter((value) => !!value)
        .map((shift) => ShiftUtils.shiftToTemplate(shift));
    });
    const fromDay = moment.unix(fromDate);
    const untilDay = moment.unix(date);

    let unsaved = unsavedShifts;
    while (fromDay.isSameOrBefore(untilDay)) {
      const dayOfWeek = fromDay.day();
      const templates = templatesByDayOfWeek[dayOfWeek] || [];
      unsaved = _(templates)
        .filter()
        .map((it) => ShiftUtils.templateToShift(fromDay, it, user))
        .reduce((acc, next) => getUpdatedUnsavedAfterInsert(next, acc), unsaved);
      fromDay.add(1, 'day');
    }
    setUnsavedShifts(unsaved);
    MiscUtils.showSuccess(
      'Repeat Until',
      'Shifts prepared successfully, click the "Save" button to save changes.',
    );
  }

  async function onApplyDeleteUntil(fromDate, date, user, ignoreNonWorking) {
    const from = moment.unix(fromDate).startOf('day').unix();
    const to = moment.unix(date).endOf('day').unix();
    await dispatch(
      api.Shifts.deleteShifts({
        from,
        to,
        clinicId,
        userId: user.id,
        ignoreNonWorking,
      }),
    );
    MiscUtils.showSuccess('Delete Shifts', 'Shifts deleted successfully');
  }

  function fillWeek(user) {
    if (!selectedTemplate) {
      MiscUtils.showToastrError('Error', 'Select a template to apply to the week');
      return;
    }
    let unsaved = unsavedShifts;
    daysOfWeek.forEach((dayNumber) => {
      if (dayNumber < 5) {
        let date = day.clone().add(dayNumber, 'day');
        let shift = ShiftUtils.templateToShift(date, selectedTemplate, user);
        unsaved = getUpdatedUnsavedAfterInsert(shift, unsaved);
      }
    });
    setUnsavedShifts(unsaved);
  }

  async function deleteWeek(user) {
    let toDelete = [];
    let toDeleteUnsaved = [];

    daysOfWeek.forEach((dayNumber) => {
      if (dayNumber < 5) {
        let date = day.clone().add(dayNumber, 'day').unix();
        let dateString = CalendarUtils.format(date, SHIFT_DATE_FORMAT);
        let shifts = ((userShifts[user.id] || [])[dateString] || []).filter(
          (shift) => !shift.non_working,
        );
        let unsaved = ((unsavedShifts[user.id] || [])[dateString] || []).filter(
          (shift) => shift.user_id === user.id,
        );
        toDelete = [...toDelete, ...shifts];
        toDeleteUnsaved = [...toDeleteUnsaved, ...unsaved];
      }
    });
    if (toDeleteUnsaved.length > 0) {
      setUnsavedShifts((prevState) => {
        let unsaved = { ...prevState };
        toDeleteUnsaved.forEach((shift) => {
          let dateString = CalendarUtils.format(shift.start, SHIFT_DATE_FORMAT);
          unsaved[user.id][dateString] = unsaved[user.id][dateString].filter((d) => d !== shift);
        });
        return unsaved;
      });
    }
    await Promise.all(toDelete.map(onDelete));
  }

  const shiftEvents = useMemo(() => {
    let shifts = [...allShifts, ...PerformanceUtils.clone(showShifts)];
    shifts = _.sortBy(shifts, ['non_working_reason', 'start']);
    return StaffUtils.smallShiftPillsToEvents(shifts, physicians, admins);
  }, [allShifts, showShifts, physicians, admins]);

  return (
    <div className={className} data-show-templates={showTemplatesOnMobile}>
      <FillModal
        user={showFillModal}
        fromDate={day.clone().add(7, 'days').unix()}
        onRequestClose={() => setShowFillModal()}
        onConfirm={onApplyRepeatWeekUntil}
      />
      <FillModal
        title="Delete Dates"
        user={showDeleteModal}
        fromDate={day.clone().unix()}
        onRequestClose={() => setShowDeleteModal()}
        onConfirm={onApplyDeleteUntil}
        ignoreNonWorking={true}
        explanation="The shifts between the selected dates will be deleted."
      />
      {!!deleteConfirmation && (
        <ConfirmationDialog
          title="Delete Shift"
          message="This will delete the shift. Are you sure you want to proceed?"
          open={!!deleteConfirmation}
          onConfirm={() => onDelete(deleteConfirmation)}
          onCancel={() => setDeleteConfirmation()}
          loadingState={deletingShift}
        />
      )}
      {editTemplatesModal && (
        <DefaultModal
          className="modal edit-shift-templates-modal"
          title="Shift Templates"
          wrap={true}
          padding={false}
          content={<ShiftTemplatesEditor />}
          isOpen={editTemplatesModal}
          onRequestClose={() => setEditTemplatesModal(false)}
        />
      )}
      {!!shiftEdit && (
        <DefaultModal
          className="modal edit-shift-modal"
          title="Edit Shift"
          wrap={true}
          padding={false}
          content={
            <EditShift shift={shiftEdit} onSave={editShift} onClose={() => setShiftEdit()} />
          }
          isOpen={!!shiftEdit}
          onRequestClose={() => setShiftEdit()}
        />
      )}
      <div className="row big-box">
        <div className="days row">
          {view === VIEWS.week && (
            <div className="calendar-week">
              <TopRow
                day={day}
                setDay={setDay}
                view={view}
                text={currentPeriodString}
                currentPeriodString={currentPeriodString}
                clinicId={clinicId}
                setClinicId={setClinicId}
                toggleView={toggleView}
              >
                <DefaultButton
                  name={buttonText}
                  disabled={numberUnsaved === 0 || savingShifts}
                  onClick={saveShifts}
                  style={{ width: '200px' }}
                />
                <DefaultButton
                  name="Show Templates"
                  onClick={setShowTemplatesOnMobile}
                  className="show-templates-button"
                />
              </TopRow>
              <div className="table-wrapper">
                <table width="100%">
                  <thead>
                    <tr>
                      <td className="user-badge">&nbsp;</td>
                      {daysOfWeek.map((dayNumber) => {
                        let date = day.clone().add(dayNumber, 'day');
                        let className = 'day';

                        if (CalendarUtils.isToday(date)) className += ' today';
                        else if (date.isBefore(today)) className += ' before';
                        return (
                          <td className={className} key={date.unix()}>
                            <span className="day-name">
                              {CalendarUtils.format(date.unix(), 'dddd')}
                            </span>
                            <span className="day-number">
                              {CalendarUtils.format(date.unix(), 'D')}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {activeUsers.map((user, index) => {
                      let total = hours[user.id] || 0;
                      let count = pendingRequests.filter(
                        ({ user_id }) => user_id === user.id,
                      ).length;
                      let className = 'user-badge';
                      if (count > 0) className += ' red';
                      if (allowShowVacations) className += ' clickable';

                      let ownRole = StaffUtils.isAdmin(user) ? 'admin' : 'physician';
                      let prevRole =
                        index > 0 && StaffUtils.isAdmin(activeUsers[index - 1])
                          ? 'admin'
                          : 'physician';

                      let changedRole = ownRole !== prevRole;
                      let roleTitle =
                        index === 0
                          ? 'Practitioners'
                          : changedRole
                          ? i18n.plural.staffTitle
                          : undefined;

                      return (
                        <>
                          {roleTitle && (
                            <tr className="small">
                              <span className="small-title">{roleTitle}</span>
                            </tr>
                          )}
                          <tr key={user.id} className="">
                            <td
                              className={className}
                              onClick={allowShowVacations ? () => onClickUser(user.id) : null}
                            >
                              <div
                                className="fill-week"
                                onClick={(event) => event.preventDefault()}
                              >
                                <Dropdown
                                  menu={{
                                    items: Object.values(DELETE_KEYS).map((label) => ({
                                      label,
                                      key: label,
                                    })),
                                    onClick: (ev) => onDeleteDropdownClick(ev, user),
                                  }}
                                  trigger={['click']}
                                >
                                  <Icon
                                    height="17px"
                                    className="red"
                                    src="calendar-xmark"
                                    alt="Delete Options"
                                    showTooltip={true}
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      return true;
                                    }}
                                  />
                                </Dropdown>
                                <Dropdown
                                  menu={{
                                    items: fillDropdownItems(true, true),
                                    onClick: (ev) => onFillDropdownClick(ev, user),
                                  }}
                                  trigger={['click']}
                                >
                                  <Icon
                                    height="17px"
                                    className="blue"
                                    src="calendar-week"
                                    alt="Fill/Repeat"
                                    showTooltip={true}
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      return true;
                                    }}
                                  />
                                </Dropdown>
                              </div>
                              {StaffUtils.isAdmin(user) && (
                                <AdminImage
                                  user={user}
                                  size={40}
                                  extra={count > 0 ? <UnreadBadge unreadCount={count} /> : null}
                                />
                              )}
                              {StaffUtils.isPhysician(user) && (
                                <PhysicianImage
                                  user={user}
                                  size={40}
                                  extra={count > 0 ? <UnreadBadge unreadCount={count} /> : null}
                                />
                              )}
                              <div className="info">
                                <span className="user-name">{StaffUtils.getName(user)}</span>
                                <span className="total-hours">{total} Hrs</span>
                              </div>
                            </td>
                            {daysOfWeek.map((dayNumber) => {
                              let date = day.clone().add(dayNumber, 'day');
                              let lookupDate = CalendarUtils.format(date.unix(), SHIFT_DATE_FORMAT);
                              let className = 'practitioner-shift';

                              let existingShifts = (userShifts[user.id] || {})[lookupDate] || [];

                              let unsaved = (unsavedShifts[user.id] || {})[lookupDate] || [];

                              //don't show the ones that are being edited
                              let unsavedIds = unsaved.map((shift) => shift.id);
                              existingShifts = existingShifts.filter(
                                (shift) => !unsavedIds.includes(shift.id),
                              );

                              let dayShifts = _.sortBy(
                                [...existingShifts, ...unsaved],
                                ['approved', 'start'],
                              );

                              if (unsaved.length > 0) className += ' unsaved';

                              let previewShift, previewShiftElement;

                              if (!!selectedTemplate) {
                                previewShift = ShiftUtils.templateToShift(
                                  date,
                                  selectedTemplate,
                                  user,
                                );
                                previewShiftElement = (
                                  <ShiftBadge
                                    shift={previewShift}
                                    color={ShiftUtils.getColor(previewShift)}
                                    className="preview"
                                    onClick={() =>
                                      addShiftFromTemplate(user, date, selectedTemplate)
                                    }
                                  />
                                );
                              }

                              let hashes = dayShifts.map((shift) => ShiftUtils.getShiftHash(shift));

                              if (
                                !!previewShift &&
                                hashes.includes(ShiftUtils.getShiftHash(previewShift))
                              ) {
                                previewShiftElement = null;
                              } else if (!previewShift) {
                                previewShiftElement = (
                                  <ShiftBadge
                                    description="Custom Shift"
                                    className="custom preview"
                                    onClick={() => {
                                      setShiftEdit({
                                        user_id: user.id,
                                        clinic_id: user.clinic_id,
                                        start: date.unix(),
                                        end: date.unix(),
                                      });
                                    }}
                                  />
                                );
                              }

                              return (
                                <td className={className} key={date.unix()}>
                                  {dayShifts.map((shift, index) => {
                                    return (
                                      <ShiftBadge
                                        key={shift.id || index}
                                        shift={shift}
                                        onClick={() => setShiftEdit(shift)}
                                        onDelete={() => onDeleteShift(shift)}
                                      />
                                    );
                                  })}
                                  {previewShiftElement}
                                </td>
                              );
                            })}
                          </tr>
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {view === VIEWS.month && (
            <div className="calendar-month">
              <TopRow
                day={day}
                setDay={setDay}
                view={view}
                text={currentPeriodString}
                clinicId={clinicId}
                setClinicId={setClinicId}
                toggleView={toggleView}
                currentPeriodString={currentPeriodString}
              />
              <TimeZoneAgnosticBigCalendar
                timeZoneName={CalendarUtils.getTargetTimezone()}
                events={shiftEvents}
                components={{
                  event: ({ event }) => <Event event={event} />,
                }}
                defaultView="month"
                toolbar={false}
                startAccessor="start"
                endAccessor="end"
                defaultDate={CalendarUtils.momentToDate(day)}
                date={CalendarUtils.momentToDate(day)}
                onNavigate={(date) => {
                  setDay(moment(date));
                }}
                selectable={false}
              />
            </div>
          )}
        </div>
        {!showVacation && view === VIEWS.week && (
          <div className="templates scroll-list full-height">
            <div className="button-wrapper">
              <DefaultButton
                name="Edit Templates"
                className="blue"
                onClick={() => {
                  setEditTemplatesModal(true);
                  setSelectedTemplate();
                }}
              />
              <DefaultButton
                name="Hide Templates"
                className="hide-templates-button red"
                onClick={() => setShowTemplatesOnMobile(false)}
              />
            </div>
            <ShiftTemplateList
              templates={templates}
              selected={selectedTemplate}
              onClick={(template) => {
                if (selectedTemplate === template) setSelectedTemplate();
                else setSelectedTemplate(template);
              }}
            />
          </div>
        )}
        {!!showVacation && (
          <Vacations
            key={showVacation}
            userId={showVacation}
            onClose={() => {
              setShowVacation();
              setShowShifts([]);
            }}
            approveMode={true}
            showCalendar={false}
            setShowShifts={setShowShifts}
            onClickedItem={goToShiftWeek}
            showFullStats={true}
          />
        )}
      </div>
    </div>
  );
};

const TopRow = ({
  day,
  setDay,
  view,
  currentPeriodString,
  toggleView,
  clinicId,
  setClinicId,
  children,
}) => {
  return (
    <div className="buttons-row">
      <DefaultButton
        name={view === VIEWS.month ? 'See Weekly View' : 'See Monthly View'}
        onClick={toggleView}
        icon="calendar-clock"
        style={{ width: '200px' }}
      />
      <ClinicSelectorWithUnread clinicId={clinicId} setClinicId={setClinicId} />
      <Navigator day={day} setDay={setDay} view={view} text={currentPeriodString} />
      {children}
    </div>
  );
};

const ClinicSelectorWithUnread = ({ clinicId, setClinicId }) => {
  const pending = useAppSelector((state) => state.shifts.pendingRequests);

  return (
    <>
      <ClinicSelector
        hideLabel={true}
        value={clinicId}
        setValue={setClinicId}
        unreadCount={(id) => pending.filter((r) => r.clinic_id === id).length}
      />
      {pending.length > 0 && (
        <div className="ml-2 mt-2">
          <UnreadBadge unreadCount={pending.length} />
        </div>
      )}
    </>
  );
};

const Navigator = ({ day, setDay, view, text }) => {
  return (
    <div className="navigator row">
      <div className="text-wrapper">
        <span className="span-button" onClick={() => setDay(day.clone().add(-1, view))}>
          {'<'}
        </span>
        <span className="description">{text}</span>
        <span className="span-button" onClick={() => setDay(day.clone().add(1, view))}>
          {'>'}
        </span>
      </div>
    </div>
  );
};

function getCurrentPeriodDescription(date, view) {
  let firstWeekDay = getFirstWeekDay(date);
  let lastWeekDay = getLastWeekDay(date);

  let result;
  if (view === VIEWS.week) {
    result = CalendarUtils.format(firstWeekDay.unix(), 'MMMM Do');
    if (firstWeekDay.month() !== lastWeekDay.month()) {
      result += ` - ${CalendarUtils.format(lastWeekDay.unix(), 'MMMM Do')}`;
    } else {
      result += ` - ${CalendarUtils.format(lastWeekDay.unix(), 'Do')}`;
    }
  } else {
    result = moment(date).format('MMMM');
  }
  return result;
}

function getFirstWeekDay(date) {
  if (!date) date = CalendarUtils.convertToTargetTimezone(moment());

  date = date.clone();
  let weekDay = date.day();
  let max = daysOfWeek[daysOfWeek.length - 1] + 1;
  if (weekDay === 0) weekDay = max;
  return date.add(-weekDay + 1, 'day');
}

function getLastWeekDay(date) {
  if (!date) date = CalendarUtils.convertToTargetTimezone(moment());

  date = date.clone();
  let weekDay = date.day();
  let max = daysOfWeek[daysOfWeek.length - 1] + 1;
  return date.add(max - weekDay, 'day');
}

function getDatesBetween(start, end) {
  let result = [];
  start = start.clone();
  while (start.isBefore(end)) {
    result.push(start.clone());
    start = start.add(1, 'day');
  }
  return result;
}

function fillDropdownItems(hasSelectedTemplate, hasShiftsInWeek) {
  const items = [];
  if (hasSelectedTemplate) {
    items.push(FILL_KEYS.fillWeek);
  }
  if (hasShiftsInWeek) {
    items.push(FILL_KEYS.repeatWeek);
  }
  return items.map((label) => ({ label, key: label }));
}

const FillModal = (props) => {
  const [date, setDate] = useState();
  const [fromDate, setFromDate] = useState(props.fromDate);
  const [ignoreNonWorking, setIgnoreNonWorking] = useState(props.ignoreNonWorking);
  const { user } = props;

  useEffect(() => {
    setFromDate(props.fromDate);
    setDate(undefined);
  }, [user, props.fromDate]);

  return (
    !!user && (
      <DefaultModal
        {...props}
        isOpen={!!user}
        title={props.title || 'Repeat Shifts'}
        wrap
        padding
        className="repeat-week-modal fit-modal"
        content={
          <>
            <DefaultDatePicker
              name="from"
              label="From Day (inclusive)"
              value={fromDate}
              className="w-full"
              onChange={(_, date) => setFromDate(date)}
            />
            <DefaultDatePicker
              name="until"
              label="Until Day (inclusive)"
              className="w-full"
              onChange={(_, date) => setDate(date)}
            />
            <span className="explanation text-xs">
              {props.explanation ||
                "The current week's shifts will be applied to all selected days."}
            </span>
            {ignoreNonWorking !== undefined && (
              <DefaultSwitch
                name="ignoreNonWorking"
                padded={false}
                onChange={(event, isInputChecked) => {
                  setIgnoreNonWorking(!isInputChecked);
                }}
                reversed={true}
                label="Delete non-working shifts"
                checked={!ignoreNonWorking}
              />
            )}
            <div className="buttons">
              <DefaultButton
                name="Cancel"
                className="cancel-button red"
                onClick={props.onRequestClose}
              />
              <DefaultButton
                name="Apply"
                className="confirm-button blue"
                onClick={() => {
                  props.onConfirm(fromDate, date, user, ignoreNonWorking);
                  props.onRequestClose();
                }}
                disabled={!date}
              />
            </div>
          </>
        }
      />
    )
  );
};

export default ShiftsEditor;
